import { convertToHTML } from 'draft-convert';
import { sanitize } from 'dompurify';
import {
  ContentState,
  convertFromRaw,
  EditorState,
  RichUtils,
  Modifier,
  ContentBlock,
  EditorChangeType,
  BlockMap,
} from 'draft-js';
import { marked } from 'marked';

import { DASH_KEY, listTypeMap, ORDERED_LIST_START, STAR_KEY } from './constants';

/**
 * @param  {string|object} text - could be a string or object, depending on if it is a lingui value or not
 * @param  {string} candidateFirstName
 * @param  {bool} translate
 *
 * @description
 * Replaces backend strings with candidateFirstName
 */
// https://stackoverflow.com/questions/15458876/check-if-a-string-is-html-or-not/15458987
export function isHTML(str: string): boolean {
  const a = document.createElement('div');
  a.innerHTML = str;

  for (let c = a.childNodes, i = c.length; i--; ) {
    if (c[i].nodeType == 1) return true;
  }

  return false;
}

export function getInnerText(html: string, replaceTagsWithSpaces = false): string {
  const div = document.createElement('div');
  div.innerHTML = sanitize(html);
  if (replaceTagsWithSpaces) {
    return div.innerHTML.replace(/<(\/?)\w+>/g, (_full, closingTag) => (closingTag ? ' ' : ''));
  }

  return div.innerText || div.innerHTML;
}

export function getHtmlFromCurrentContent(currentContent: ContentState): string {
  const newContent = convertToHTML({})(currentContent);
  const html = sanitize(newContent);
  const div = document.createElement('div');
  div.innerHTML = html;
  if (div.innerText === '') {
    return '';
  }
  return html;
}

export function maybeConvertDraftToHtml(value: string): string {
  let html = '';
  try {
    const content = convertFromRaw(JSON.parse(value));
    html = getHtmlFromCurrentContent(content);
  } catch (e) {
    html = getInnerText(
      marked.parseInline((value || '').toString(), {
        xhtml: true,
      })
    );
  }
  // Detect if there are any html tags inside
  if (isHTML(html)) {
    return html;
  }
  return html;
}

export const isEmptyListItem = (block: ContentBlock): boolean => {
  const text = block.getText();
  const hasEmptyText = text.length === 0;
  const blockType = block.getType();
  const isListItemBlock = blockType === 'unordered-list-item' || blockType === 'ordered-list-item';

  return isListItemBlock && hasEmptyText;
};

export const getSelectedBlock = (editorState: EditorState): ContentBlock => {
  const selection = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const blockStartKey = selection.getStartKey();

  return contentState.getBlockMap().get(blockStartKey);
};

function updateBlockDepth(
  block: ContentBlock,
  editorState: EditorState,
  decrease = false
): EditorState {
  const blockKey = block.getKey();
  const depth = block.getDepth();
  const newBlock = block.set('depth', depth + (decrease ? -1 : 1)) as ContentBlock;
  const contentState = editorState.getCurrentContent();
  const blockMap = contentState.getBlockMap();
  const newBlockMap = blockMap.set(blockKey, newBlock) as BlockMap;
  return EditorState.push(
    editorState,
    contentState.merge({ blockMap: newBlockMap }) as ContentState,
    'adjust-depth'
  );
}

export const decreaseBlockDepth = (block: ContentBlock, editorState: EditorState): EditorState =>
  updateBlockDepth(block, editorState, true);

export const increaseBlockDepth = (block: ContentBlock, editorState: EditorState): EditorState =>
  updateBlockDepth(block, editorState);

export const changeBlockType = (editorState: EditorState): EditorState =>
  EditorState.push(
    editorState,
    RichUtils.tryToRemoveBlockStyle(editorState) as ContentState,
    'change-block-type'
  );

export const shouldStartList = (block: ContentBlock): boolean => {
  const text = block.getText();
  return (
    block.getType() === 'unstyled' &&
    block.getDepth() === 0 &&
    (text === STAR_KEY || text === DASH_KEY || text === ORDERED_LIST_START)
  );
};

export const startList = (
  editorState: EditorState,
  block: ContentBlock,
  command: string
): EditorState => {
  const listType = listTypeMap[
    command as 'start-unordered-list' | 'start-ordered-list'
  ] as EditorChangeType;
  const newEditorState = RichUtils.toggleBlockType(editorState, listType);
  const contentState = newEditorState.getCurrentContent();
  const selection = newEditorState.getSelection();

  const blockSelection = selection.merge({
    anchorOffset: 0,
    focusOffset: block.getLength(),
  });

  const newContentState = Modifier.replaceText(contentState, blockSelection, '');

  return EditorState.push(newEditorState, newContentState, 'insert-fragment');
};

export const insertSpace = (editorState: EditorState): EditorState => {
  const contentState = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const newContent = Modifier.insertText(contentState, selection, ' ');

  return EditorState.push(editorState, newContent, 'insert-fragment');
};

export const isValuePlainText = (value: string): boolean => {
  try {
    const content = convertFromRaw(JSON.parse(value));
    return !content;
  } catch {
    return true;
  }
};
